import React, { useState } from "react";
import { useRole } from "../context/RoleContext";
import { runReport, adminExportUrl } from "../lib/adminPortal";
import { Download, Play, ChevronDown, FileText } from "lucide-react";

/**
 * Platform Admin · Operational Reports
 *
 * Editorial single-panel layout. Each report is its own card with the
 * parameter inputs inline and an inline "Run" action. The results table
 * expands within the same card and the card snaps to full page width
 * (gridColumn: 1 / -1) so wide tables breathe. Mirrors the inline View
 * pattern used by CityReports — no slide-over drawers, no modal
 * masking, no cropped 960px scroll viewport.
 */

const REPORTS = [
  {
    kind: "architect_roster",
    title: "Architect Roster.",
    desc: "Every verified architect with firm, service areas, specialisations, design counts, and project counts.",
    params: [
      {
        key: "period",
        label: "Period",
        type: "select",
        options: [
          { v: "all", label: "All time" },
          { v: "30d", label: "Last 30 days" },
          { v: "quarter", label: "Last quarter" },
          { v: "ytd", label: "Year to date" },
        ],
      },
      { key: "include_deactivated", label: "Include deactivated", type: "toggle" },
    ],
  },
  {
    kind: "applications_throughput",
    title: "Application Throughput.",
    desc: "Applications submitted, approved, more-info-requested, and rejected within a period, with median time to decision.",
    params: [
      {
        key: "period",
        label: "Period",
        type: "select",
        options: [
          { v: "all", label: "All time" },
          { v: "30d", label: "Last 30 days" },
          { v: "quarter", label: "Last quarter" },
          { v: "ytd", label: "Year to date" },
        ],
      },
    ],
  },
  {
    kind: "design_catalog_status",
    title: "Design Catalog Status.",
    desc: "Every design with status, architect, typology, cost, and date last updated.",
    params: [
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { v: "all", label: "All statuses" },
          { v: "Approved", label: "Approved" },
          { v: "In Review", label: "In Review" },
          { v: "Needs Revision", label: "Needs Revision" },
          { v: "Rejected", label: "Rejected" },
          { v: "Draft", label: "Draft" },
        ],
      },
      {
        key: "typology",
        label: "Typology",
        type: "select",
        options: [
          { v: "all", label: "All typologies" },
          { v: "single_family", label: "Single family" },
          { v: "duplex", label: "Duplex" },
          { v: "adu", label: "ADU" },
          { v: "multi_family", label: "Multi-family" },
        ],
      },
    ],
  },
  {
    kind: "project_pipeline",
    title: "Project Pipeline.",
    desc: "Every project with stage, parcel, design, architect, owner, and current state.",
    params: [
      {
        key: "stage",
        label: "Stage",
        type: "select",
        options: [
          { v: "all", label: "All stages" },
          { v: "Selection", label: "Selection" },
          { v: "Consultation", label: "Consultation" },
          { v: "Purchase", label: "Purchase" },
          { v: "Expedited Review", label: "Expedited Review" },
          { v: "Build", label: "Build" },
          { v: "Construction Inspection", label: "Construction Inspection" },
          { v: "Complete", label: "Complete" },
        ],
      },
      {
        key: "neighborhood",
        label: "Neighborhood",
        type: "select",
        options: [
          { v: "all", label: "All neighborhoods" },
          { v: "Near Eastside", label: "Near Eastside" },
          { v: "Martindale-Brightwood", label: "Martindale-Brightwood" },
          { v: "Other Indianapolis", label: "Other Indianapolis" },
        ],
      },
    ],
  },
];

function defaultParams(def) {
  const initial = {};
  for (const p of def.params) {
    if (p.type === "toggle") initial[p.key] = false;
    else initial[p.key] = p.options?.[0]?.v;
  }
  return initial;
}

export default function AdminReports() {
  const { user } = useRole();
  return (
    <section className="animate-fade-in-up" data-testid="admin-reports-page">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 28,
            height: 2,
            background: "#B14E2F",
          }}
        />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Platform Admin · Reports
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(26px, 3.6vw, 32px)",
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Operational Reports.
      </h1>
      <p
        style={{
          color: "#6C6B85",
          fontSize: 14,
          marginTop: 6,
          maxWidth: 720,
          lineHeight: 1.55,
        }}
      >
        Pre-canned parameterised queries. Choose parameters, run a report
        to see a document-grade table inline, then export the result to
        CSV.
      </p>

      <div style={{ borderTop: "1px solid #D9D8E4", marginTop: 18 }} />

      <div
        className="mt-6 grid gap-4"
        style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        data-testid="admin-reports-grid"
      >
        {REPORTS.map((def) => (
          <ReportCard key={def.kind} def={def} userId={user?.id} />
        ))}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Card                                                                    */
/* ────────────────────────────────────────────────────────────────────── */

function ReportCard({ def, userId }) {
  const [params, setParams] = useState(() => defaultParams(def));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const onRun = async () => {
    if (!userId) return;
    setBusy(true);
    setErr(null);
    try {
      const data = await runReport(def.kind, userId, params);
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not run report.");
    } finally {
      setBusy(false);
    }
  };

  const onHide = () => {
    setResult(null);
    setErr(null);
  };

  const expanded = !!result || !!err;

  return (
    <div
      className="wn-card"
      style={{
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // Wide tables need room — when results are open, span both columns.
        gridColumn: expanded ? "1 / -1" : undefined,
      }}
      data-testid={`admin-reports-row-${def.kind}`}
    >
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          flex: 1,
        }}
      >
        {/* Title + blurb */}
        <div>
          <div className="flex items-center gap-2">
            <FileText size={15} style={{ color: "#1B1F4B" }} />
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 17,
                color: "#1B1F4B",
                lineHeight: 1.2,
              }}
            >
              {def.title}
            </h2>
          </div>
          <p
            style={{
              color: "#6C6B85",
              fontSize: 12.5,
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {def.desc}
          </p>
        </div>

        {/* Inline parameter row */}
        <div
          className="flex flex-wrap items-end"
          style={{ gap: "10px 18px" }}
          data-testid={`admin-reports-params-${def.kind}`}
        >
          {def.params.map((p) => (
            <ParamField
              key={p.key}
              def={def}
              p={p}
              value={params[p.key]}
              onChange={(v) => setParams({ ...params, [p.key]: v })}
            />
          ))}
        </div>

        {/* Action row */}
        <div
          className="flex items-center justify-between gap-3 flex-wrap"
          style={{
            paddingTop: 10,
            borderTop: "1px solid #E8E7EE",
          }}
        >
          <span
            className="tabular-nums"
            style={{
              fontSize: 11,
              color: "#6C6B85",
              letterSpacing: "0.04em",
            }}
            data-testid={`admin-reports-meta-${def.kind}`}
          >
            {result
              ? `${result.total} ${result.total === 1 ? "row" : "rows"} · run ${new Date(
                  result.run_at,
                ).toLocaleString()}`
              : "Awaiting parameters"}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {expanded && (
              <button
                type="button"
                onClick={onHide}
                className="wn-btn wn-btn-outline inline-flex items-center"
                style={{ gap: 6, fontSize: 12 }}
                data-testid={`admin-reports-hide-${def.kind}`}
              >
                Hide
                <ChevronDown
                  size={13}
                  style={{ transform: "rotate(180deg)" }}
                />
              </button>
            )}
            <button
              type="button"
              onClick={onRun}
              disabled={busy || !userId}
              className="wn-btn wn-btn-primary inline-flex items-center"
              style={{ gap: 6, fontSize: 12 }}
              data-testid={`admin-reports-run-${def.kind}`}
            >
              <Play size={13} />
              {busy ? "Running…" : result ? "Re-run" : "Run report"}
            </button>
            {result && result.total > 0 && (
              <a
                href={adminExportUrl(def.kind, userId, params)}
                className="wn-btn wn-btn-tertiary inline-flex items-center"
                style={{ gap: 6, fontSize: 12 }}
                data-testid={`admin-reports-csv-${def.kind}`}
              >
                <Download size={13} /> CSV
              </a>
            )}
          </div>
        </div>
      </div>

      {err && (
        <div
          style={{
            borderTop: "1px solid #D9D8E4",
            background: "#FBFAFD",
            padding: "14px 20px",
            color: "#A23B2E",
            fontSize: 13,
          }}
          data-testid={`admin-reports-error-${def.kind}`}
        >
          {err}
        </div>
      )}

      {result && <ResultsTable result={result} kind={def.kind} />}
    </div>
  );
}

function ParamField({ def, p, value, onChange }) {
  if (p.type === "select") {
    return (
      <div style={{ minWidth: 180 }}>
        <label
          className="wn-label block"
          style={{ color: "#6C6B85", marginBottom: 4 }}
        >
          {p.label}
        </label>
        <select
          className="wn-input"
          value={value ?? p.options[0].v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`admin-reports-param-${def.kind}-${p.key}`}
          style={{ width: "100%" }}
        >
          {p.options.map((o) => (
            <option key={o.v} value={o.v}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    );
  }
  if (p.type === "toggle") {
    return (
      <label
        className="flex items-center gap-2"
        style={{
          fontSize: 13,
          color: "#272636",
          padding: "8px 12px",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          background: "#FBFAFD",
          cursor: "pointer",
          height: 38,
        }}
      >
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`admin-reports-param-${def.kind}-${p.key}`}
        />
        <span>{p.label}</span>
      </label>
    );
  }
  return null;
}

function ResultsTable({ result, kind }) {
  const rows = result.rows || [];
  if (rows.length === 0) {
    return (
      <div
        style={{
          borderTop: "1px solid #D9D8E4",
          background: "#FBFAFD",
          padding: "16px 20px",
        }}
        data-testid={`admin-reports-empty-${kind}`}
      >
        <p
          style={{
            margin: 0,
            color: "#6C6B85",
            fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
            fontSize: 13,
          }}
        >
          No records match the supplied parameters.
        </p>
      </div>
    );
  }
  const cols = Object.keys(rows[0]);
  return (
    <div
      style={{
        borderTop: "1px solid #D9D8E4",
        background: "#FBFAFD",
      }}
      data-testid={`admin-reports-results-${kind}`}
    >
      <div style={{ overflow: "auto", maxHeight: 480 }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12.5,
            tableLayout: "auto",
          }}
          data-testid={`admin-reports-table-${kind}`}
        >
          <thead>
            <tr style={{ background: "#1B1F4B", color: "#FBFAFD" }}>
              {cols.map((c) => (
                <th
                  key={c}
                  scope="col"
                  style={{
                    padding: "8px 14px",
                    textAlign: "left",
                    fontSize: 10,
                    letterSpacing: "0.06em",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    position: "sticky",
                    top: 0,
                    borderRight: "1px solid rgba(251,250,253,0.12)",
                    whiteSpace: "nowrap",
                    zIndex: 1,
                  }}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  background: i % 2 === 0 ? "#FBFAFD" : "#F3F2F6",
                }}
                data-testid={`admin-reports-row-${kind}-${i}`}
              >
                {cols.map((c, j) => {
                  const v = row[c];
                  const numeric = typeof v === "number";
                  return (
                    <td
                      key={c}
                      style={{
                        padding: "7px 14px",
                        color: "#272636",
                        verticalAlign: "top",
                        borderBottom: "1px solid #E8E7EE",
                        textAlign: numeric ? "right" : "left",
                        whiteSpace: "nowrap",
                      }}
                      className={numeric || j > 0 ? "tabular-nums" : ""}
                    >
                      {v == null || v === "" ? (
                        <span style={{ color: "#9AA0C0" }}>—</span>
                      ) : (
                        String(v)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
